/**
 * Number / time formatting for the trading terminal.
 * Memecoin prices span 15 orders of magnitude, so tiny prices use the
 * subscript-zero notation traders expect: $0.0₅4387 = $0.000004387.
 */

const SUB = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
const subscript = (n: number) => String(n).split('').map((d) => SUB[Number(d)]).join('');

export function fmtPrice(v: number | undefined, prefix = '$'): string {
  if (v === undefined || !Number.isFinite(v)) return '—';
  if (v === 0) return `${prefix}0`;
  const abs = Math.abs(v);
  if (abs >= 1000) return `${prefix}${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (abs >= 1) return `${prefix}${abs.toFixed(abs >= 100 ? 2 : 3)}`;
  if (abs >= 0.001) return `${prefix}${abs.toFixed(5).replace(/0+$/, '')}`;
  // 0.0000043871 -> zeros after the point = 5 -> 0.0₅4387
  const zeros = Math.floor(-Math.log10(abs)) - 1;
  const digits = Math.round(abs * 10 ** (zeros + 4));
  return `${prefix}0.0${subscript(zeros)}${digits}`;
}

export function fmtUsd(v: number | undefined, digits = 1): string {
  if (v === undefined || !Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(digits)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(digits)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(digits)}K`;
  if (abs >= 1) return `${sign}$${abs.toFixed(abs >= 100 ? 0 : 2)}`;
  return `${sign}$${abs.toFixed(2)}`;
}

export function fmtNum(v: number | undefined, digits = 1): string {
  if (v === undefined || !Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(digits)}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(digits)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(digits)}K`;
  return `${sign}${abs.toFixed(abs >= 10 ? 0 : 2)}`;
}

export function fmtSol(v: number | undefined, signed = false): string {
  if (v === undefined || !Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  const s = abs >= 100 ? abs.toFixed(0) : abs >= 1 ? abs.toFixed(2) : abs >= 0.01 ? abs.toFixed(3) : abs.toFixed(4);
  const sign = v < 0 ? '-' : signed && v > 0 ? '+' : '';
  return `${sign}${s}`;
}

export function fmtPct(v: number | undefined, digits = 2): string {
  if (v === undefined || !Number.isFinite(v)) return '—';
  return `${v.toFixed(v >= 10 ? 1 : digits)}%`;
}

/** "12s", "4m", "3h", "2d" — compact age like trading terminals. */
export function fmtAge(ts: number | undefined, nowSec = Date.now() / 1000): string {
  if (!ts) return '—';
  const d = Math.max(0, Math.round(nowSec - ts));
  if (d < 60) return `${d}s`;
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86_400) return `${Math.floor(d / 3600)}h`;
  if (d < 86_400 * 60) return `${Math.floor(d / 86_400)}d`;
  return `${Math.floor(d / (86_400 * 30))}mo`;
}

export const shortAddr = (a: string | undefined, head = 4, tail = 4) => (a ? `${a.slice(0, head)}…${a.slice(-tail)}` : '—');

export const solscan = (a: string) => `https://solscan.io/account/${a}`;
export const solscanTx = (s: string) => `https://solscan.io/tx/${s}`;
